import {
  Component,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  HostListener
} from "@angular/core";
import { PreviousRouteService } from "src/app/core/services/previous-route.service";
import { LocationStrategy } from "@angular/common";
import { PersonsService } from "src/app/core/services/persons.service";
import PersonsModel from "src/app/core/models/persons.model";
import { ProviderVisitsService } from "src/app/core/services/provider-visits.service";
import ProviderVisitsModel from "src/app/core/models/provider-visits.model";
import { ProviderVisitsStatsService } from "src/app/core/services/provider-visits-stats.service";
import { ProductPlacementsService } from "src/app/core/services/product-placements.service";
import LocationTrackerModel from "src/app/core/models/location-tracker.model";
import { LocationTrackerService } from "src/app/core/services/location-tracker.service";
import { GoogleMap, MapInfoWindow, MapMarker } from '@angular/google-maps';
import { ToastrService } from "ngx-toastr";
import { BusinessEntityService } from "src/app/core/services/business.service";
import { ExcelService } from "src/app/core/services/excel.service";
import { ClimateParamsService } from "src/app/core/services/climate-params.service";
import { FormGroup, FormBuilder, Validators } from "@angular/forms";
import { cloneDeep } from 'lodash';
import * as moment from 'moment';
import { timeout } from 'rxjs/operators';
import { USER_SESSION_SERVICE } from "src/app/core/rbacl/user-session.service";
import { PERSON_ROLE_KEYS } from "src/app/core/constants/app-constants";

declare var $: any;
declare const google: any;

/**
 * @component ActivityTrackerCopyComponent
 * @selector app-activity-tracker-copy
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `ActivityTrackerCopyComponent` is a comprehensive reporting dashboard designed for tracking staff activities,
 * specifically focusing on provider visits, product placements, and field operations. It serves as a central hub for
 * monitoring field force performance and gathering market intelligence.
 *
 * ## Key Responsibilities:
 * 1. **Visit Tracking**: Monitors visits made by technicians and managers to farmers, dealers, and consultants.
 * 2. **Data Visualization**: Presents visit data in both tabular formats and interactive Google Maps with location markers.
 * 3. **Hierarchical Filtering**: Allows filtering data based on the organizational hierarchy (Business -> Manager -> Technician).
 * 4. **Statistical Aggregation**: Displays aggregated stats for products, dues, harvests, and other key performance indicators.
 * 5. **Report Generation**: Generates specific reports for Dealer Visits, Dealer Payments, Farmer Visits, Consultant Visits, and Harvest Summaries.
 * 6. **Visit Management**: Allows authorized users (e.g., Managers) to approve or reject visits with comments.
 * 7. **Export Functionality**: Supports exporting filtered report data to Excel for offline analysis.
 *
 * ## User Roles & Permissions:
 * - **Business Admin**:
 *   - Can view data for all managers and technicians within the business.
 *   - Can filter by any manager or technician.
 * - **Provider Manager**:
 *   - Can view data for technicians reporting directly to them.
 *   - Restricted to their own hierarchy.
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Fetches the list of managers and technicians associated with the business (`initialDataOnPageLoad`).
 *    - Loads initial statistical aggregates.
 * 2. **View Switching**:
 *    - Users can toggle between 'Tabular View' and 'Map View'.
 *    - Data is loaded lazily based on the selected view to optimize performance (`toogleVisitsViewState`).
 *    - Images associated with visits are downloaded in parallel batches to prevent UI blocking.
 * 3. **Filtering**:
 *    - Selecting a Manager filters the Technician list.
 *    - Selecting a Technician filters the visits and stats.
 *    - Date range and status filters are applied client-side or trigger new data fetches depending on the context.
 * 4. **Reporting**:
 *    - Clicking on specific report types (e.g., 'Dealer Visits') triggers `exploreReportView`.
 *    - This fetches specific datasets and configures the view for that report type.
 *
 * ## UI State Management:
 * - **Loaders**: Uses multiple flags (`initialPageLoader`, `tabularViewLoader`, `mapsViewLoader`) to manage loading states for different sections.
 * - **Expand/Collapse**: Manages visibility of aggregate sections and report sections.
 * - **Modals**: Handles visit details and image previews via custom modals.
 */
@Component({
  selector: "app-visits",
  templateUrl: "./visits.component.html",
  styleUrls: ["./visits.component.scss"],
})
export class VisitsComponent implements OnInit, OnDestroy {

  // Maanager and technician dropdown related varibales
  /** @type {boolean} Flag to control manager dropdown visibility. */
  public managerDropdownOpen: boolean;
  /** @type {string} Name of the selected manager. */
  public selectedManager: string;
  /** @type {any} Object containing details of the selected manager. */
  public selectedManagerObj: any;
  /** @type {boolean} Flag indicating if a manager is selected. */
  public isManagerSelected: boolean;
  /** @type {boolean} Flag to control technician dropdown visibility. */
  public technicianDropdownOpen: boolean;
  /** @type {string} Name of the selected technician. */
  public selectedTechnician: string;
  /** @type {any} Object containing details of the selected technician. */
  public selectedTechnicianObj: any;
  /** @type {boolean} Flag indicating if a technician is selected. */
  public isTechnicianSelected: boolean;
  /** @type {Array<PersonsModel>} Copy of the provider managers list for filtering. */
  public $providerManagersListCpy: Array<PersonsModel>;
  /** @type {Array<PersonsModel>} Copy of the provider technicians list for filtering. */
  public $providerTechniciansListCpy: Array<PersonsModel>;
  /** @type {any} Filtered list of provider managers. */
  public $providerManagersList: any;
  /** @type {any} Filtered list of provider technicians. */
  public $providerTechniciansList: any;

  // Provider visit stats data related variables
  /** @type {any} Original provider visit statistics data. */
  public providerVisitsStatsOriginal: any;
  /** @type {any} Copy of provider visit statistics data. */
  public providerVisitsStatsCpy: any;
  /** @type {any} Current provider visit statistics data displayed. */
  public providerVisitsStats: any;

  // Provider visits data related variables
  /** @type {Array<ProviderVisitsModel>} Original list of provider visits. */
  public providerVisitsListOriginal: Array<ProviderVisitsModel> = [];
  /** @type {Array<ProviderVisitsModel>} Copy of provider visits list for filtering. */
  public providerVisitsListCpy: Array<ProviderVisitsModel> = [];
  /** @type {Array<ProviderVisitsModel>} Current list of provider visits displayed. */
  public providerVisitsList: Array<ProviderVisitsModel> = [];
  /** @type {ProviderVisitsModel} Currently selected visit object for details view. */
  public visitObject: ProviderVisitsModel;
  /** @type {any} Detailed information of the selected visit. */
  public visitObjectDetails: any;

  // Block display related variables
  /** @type {boolean} Flag to show tabular view of visits. */
  public isTabularView: boolean;
  /** @type {boolean} Flag to show map view of visits. */
  public isMapView: boolean;
  /** @type {boolean} Flag indicating if approval button was clicked. */
  public isApprovalButtonClicked: boolean;
  /** @type {boolean} Flag to display product stats block. */
  public isProductsStatsBlockDisplay: boolean;
  /** @type {boolean} Flag to display due stats block. */
  public isDueStatsBlockDisplay: boolean;
  /** @type {boolean} Flag to display harvest stats block. */
  public isHarvestStatsBlockDisplay: boolean;
  /** @type {boolean} Flag to display placements data. */
  public isPlacementsDataDisplay: boolean;
  /** @type {boolean} Flag to display movements data. */
  public isMovementsDataDisplay: boolean;
  /** @type {boolean} Flag to display returns data. */
  public isReturnsDataDisplay: boolean;
  /** @type {boolean} Flag to display payments data. */
  public isPaymentsDataDisplay: boolean;
  /** @type {boolean} Flag to display product usage data. */
  public isProductUsageDataDisplay: boolean;
  /** @type {boolean} Flag to display product discussed data. */
  public isProductDiscussedDataDisplay: boolean;
  /** @type {boolean} Flag indicating if products usage is displayed. */
  public isProductsUsageDisplayed: boolean;
  /** @type {boolean} Flag indicating if harvest summary is displayed. */
  public isHarvestSummaryDisplayed: boolean;
  /** @type {boolean} Flag indicating if web view options are clicked (responsive). */
  public isWebViewOptionsClicked: boolean;
  /** @type {boolean} Flag to control card image popup visibility. Default `false`. */
  public isCardImagePopupVisible: boolean = false;
  /** @type {string | null} Selected image URL/Base64 for popup. Default `null`. */
  public selectedImage: string | null = null;
  /** @type {boolean} Flag to control date range popup visibility. Default `false`. */
  public isDateRangePopupOpen: boolean = false;

  // Login user related variables
  /** @type {string} Role of the logged-in person. */
  public loginPersonType: string;
  /** @type {string} Category of the logged-in business. */
  public loginBusinessCategory: string;
  /** @type {string} ID of the logged-in business. */
  public loginbusinessID: string;
  /** @type {string} ID of the logged-in person. */
  public loginPersonId: string;

  // Loaders related variables
  /** @type {boolean} Flag for initial page loading state. */
  public initialPageLoader: boolean;
  /** @type {boolean} Flag for tabular view loading state. */
  public tabularViewLoader: boolean;
  /** @type {boolean} Flag for map view loading state. */
  public mapsViewLoader: boolean;
  /** @type {boolean} Flag for visit details loading state. */
  public visitDetailsLoader: boolean;

  // Filters block related variables
  /** @type {boolean} Flag indicating if status filter is active. */
  public isStatusFilterSelected: boolean;
  /** @type {boolean} Flag indicating if date filter is active. */
  public isDateFilterSelected: boolean;
  /** @type {string} Search keyword. */
  public search: string;
  /** @type {string} Selected visit status for filtering. */
  public selectedVisitStatus: string;
  /** @type {string} Selected date filter type (e.g., 'Today', '1W'). */
  public selectedDateFilter: string;

  // Visits Map View Related variables
  /** @type {Array<LocationTrackerModel>} Original location tracking details. */
  public locationTrackingDetailsOriginal: Array<LocationTrackerModel>;
  /** @type {Array<LocationTrackerModel>} Copy of location tracking details for filtering. */
  public locationTrackingDetailsCpy: Array<LocationTrackerModel>;
  /** @type {any} Current location tracking details displayed on map. */
  public locationTrackingDetails: any;
  /** @type {any} Google Map instance. */
  map: any;
  /** @type {ProviderVisitsModel} Details for the info window on map. */
  public locationTrackingInfoDetails: ProviderVisitsModel;
  /** @type {boolean} Flag indicating if map is in full screen mode. Default `false`. */
  private isMapViewInFullScreenMode = false;
  /** @type {GoogleMap} Reference to the Google Map component. */
  @ViewChild('gm', { static: false }) mapElement!: GoogleMap;
  /** @type {MapInfoWindow} Reference to the Map Info Window component. */
  @ViewChild(MapInfoWindow) infoWindow!: MapInfoWindow;
  /** @type {boolean} Flag indicating if info window is opened. Default `false`. */
  public infoWindowOpened: boolean = false;
  /** @type {null} Zoom level for map. */
  public zoom: null;
  /** @type {null} Latitude for map center. */
  public lat = null;
  /** @type {null} Longitude for map center. */
  public lng = null;

  // status update related temp variables
  /** @type {string} Comment for visit status update. */
  public visitComment: string;

  // Sorting variables
  /** @type {boolean} Flag for sorting order. Default `true` (Ascending). */
  public isSortedAsc: boolean = true; // State to track the sort order

  // Date Range Variables
  /** @type {string} Custom start date for filtering. */
  public customFromDate: string;
  /** @type {string} Custom end date for filtering. */
  public customToDate: string;
  /** @type {any} Google Map instance reference (duplicate?). */
  public gm: any;
  /** @type {boolean} Flag to expand/collapse aggregates section. Default `true`. */
  public isAggregatesExpanded: boolean = true; // Tracks whether the section is expanded
  /** @type {boolean} Flag to expand/collapse reports section. Default `true`. */
  public isReportsExpanded: boolean = true; // Tracks whether the section is expanded
  /** @type {FormGroup} Form group for request submission. */
  public requestSubmissionFrom: FormGroup;
  /** @type {boolean} Flag indicating if form is submitted. */
  public submitted: boolean;

  /** @type {boolean} Flag for reports loading state. */
  public reportsLoader: boolean;
  /** @type {boolean} Flag indicating if a report view is clicked. */
  public isReportViewClicked: boolean;
  /** @type {boolean} Flag for Dealer Visit Report active state. */
  public isDealerVisitReport: boolean;
  /** @type {boolean} Flag for Dealer Payment Report active state. */
  public isDealerPaymentReport: boolean;
  /** @type {boolean} Flag for Farmer Visit Report active state. */
  public isFarmerVisitReport: boolean;
  /** @type {boolean} Flag for Consultant Visit Report active state. */
  public isConsultantVisitReport: boolean;
  /** @type {boolean} Flag for Harvest Summary Report active state. */
  public isHarvestSummaryReport: boolean;
  /** @type {string} Search string for reports. Default `''`. */
  public reportSearch: string = '';
  /** @type {Array<any>} List of dealer visits. */
  public dealervisits = [];
  /** @type {Array<any>} Copy of dealer visits list. */
  public dealervisitsCpy = [];
  /** @type {Array<any>} List of dealer payments. */
  public dealerpayments = [];
  /** @type {Array<any>} Copy of dealer payments list. */
  public dealerpaymentsCpy = [];
  /** @type {Array<any>} List of farmer visits. */
  public farmerVisits = [];
  /** @type {Array<any>} Copy of farmer visits list. */
  public farmerVisitsCpy = [];
  /** @type {Array<any>} List of consultant visits. */
  public consultantVisits = [];
  /** @type {Array<any>} Copy of consultant visits list. */
  public consultantVisitsCpy = [];
  /** @type {Array<any>} List of harvest summaries. */
  public harvestSummaries = [];
  /** @type {Array<any>} Copy of harvest summaries list. */
  public harvestSummariesCpy = [];
  /** @type {boolean} Flag indicating if location filter is selected. */
  public isLocationSelected: boolean;
  /** @type {boolean} Flag indicating if name filter is selected. */
  public isNameSelected: boolean;
  /** @type {boolean} Flag indicating if technician association filter is selected. */
  public isAssociationTechnicianSelected: boolean;
  /** @type {boolean} Flag indicating if farm business name filter is selected. */
  public isFarmBusinessNameSelected: boolean;
  /** @type {boolean} Flag indicating if customer type filter is selected. */
  public isTypeofCustomerSelected: boolean;
  /** @type {boolean} Flag indicating if date range filter is selected. */
  public isDateRangeSelected: boolean;
  /** @type {boolean} Flag indicating if visit report date range popup is open. */
  public isVisitReportDateRangePopupOpen: boolean;
  /** @type {string} Name of the visitor for filtering. */
  public visitorName: string;
  /** @type {string} Name of the location for filtering. */
  public locationName: string;
  /** @type {string} Type of customer for filtering. */
  public typeOfCustomer: string;
  /** @type {string} Name of the technician for filtering. */
  public technicianName: string;
  /** @type {string} Name of the farm business for filtering. */
  public farmBusinessName: string;
  /** @type {string} Placeholder text for search input. */
  public searchBy: string;
  /** @type {boolean} Flag indicating if an error occurred. */
  public isErrorOccured: boolean;

  /** @type {Date} Maximum allowed date for date picker. */
  maxDate = new Date();
  /** @type {FormGroup} Form group for date range picker. */
  dateRangeForm!: FormGroup;
  /** @type {{startDate: moment.Moment, endDate: moment.Moment}} Selected date range. */
  selected: { startDate: moment.Moment, endDate: moment.Moment };

  /**
   * Constructor for ActivityTrackerCopyComponent.
   * Initializes services and sets up the request submission form.
   *
   * @param {PersonsService} personsService - Service for managing person data.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   * @param {LocationStrategy} locationStrategy - Service for interacting with browser URL.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {ProviderVisitsService} providerVisitsService - Service for managing provider visits.
   * @param {ProviderVisitsStatsService} providerVisitsStatsService - Service for provider visit statistics.
   * @param {ProductPlacementsService} productPlacementsService - Service for product placements.
   * @param {LocationTrackerService} locationTrackerService - Service for location tracking.
   * @param {Renderer2} renderer - Service for DOM manipulation.
   * @param {ToastrService} toasterService - Service for displaying toast notifications.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {ClimateParamsService} aquService - Service for climate parameters.
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   *
   * @service PersonsService
   * @service PreviousRouteService
   * @service LocationStrategy
   * @service ExcelService
   * @service ProviderVisitsService
   * @service ProviderVisitsStatsService
   * @service ProductPlacementsService
   * @service LocationTrackerService
   * @service Renderer2
   * @service ToastrService
   * @service BusinessEntityService
   * @service TransactionsService
   * @service ClimateParamsService
   * @service FormBuilder
   */
  constructor(
    public personsService: PersonsService,
    private previousRouteService: PreviousRouteService,
    private locationStrategy: LocationStrategy,
    public excelService: ExcelService,
    private providerVisitsService: ProviderVisitsService,
    private providerVisitsStatsService: ProviderVisitsStatsService,
    private productPlacementsService: ProductPlacementsService,
    private locationTrackerService: LocationTrackerService,
    private renderer: Renderer2,
    private toasterService: ToastrService,
    public businessEntityService: BusinessEntityService,
    public aquService: ClimateParamsService,
    public formBuilder: FormBuilder
  ) {
    /**
    * Initializing Quick Registration form for validation.
    * This FormGroup represents the form controls for the Quick Registration data.
    * @type {FormGroup}
    * @type {FormControl}
    */
    this.requestSubmissionFrom = this.formBuilder.group({
      comment: ['', [Validators.required,
      Validators.maxLength(200)
      ]],
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Sets up previous route tracking.
   * - Initializes date picker and default filters.
   * - Loads initial data for the page.
   * - Hides report views initially.
   */
  public async ngOnInit() {
    this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
    this.isProductsStatsBlockDisplay = true;
    this.isWebViewOptionsClicked = window.innerWidth < 525 ? true : false;
    this.customFromDate = '';
    this.customToDate = '';
    this.loginPersonType = localStorage.getItem("loginPersonType");
    this.initializeDatePicker()
    // To initialize required data on page load
    this.initialDataOnPageLoad();
    // To intialize default filter variables on page load
    this.applyInitialDefaultFilters();
    // To hide reports and report loader
    this.hideExploreReportView();
  }

  /**
    * @method
    * @description Initializes the date picker form group and subscribes to value changes.
    * @returns {void}
    * @side-effect Creates `dateRangeForm` and updates `isDateRangeSelected`.
    */
  public initializeDatePicker(): void {
    this.dateRangeForm = this.formBuilder.group({
      start: [null],
      end: [null]
    });

    this.dateRangeForm.valueChanges.subscribe(val => {
      const { start, end } = val;
      if (!start || !end) {
        this.isDateRangeSelected = false;
      }
    });
  }

  /**
   * @method onPickerClosed
   * @description Handles the closing of the date picker. Ensures valid range and triggers filter application.
   * @side-effect Updates `dateRangeForm`, `isDateRangeSelected`, calls `onDateChanged`.
   */
  public onPickerClosed(): void {
    const { start, end } = this.dateRangeForm.value;

    if (start && !end) {
      // Only start selected → default end = start
      this.dateRangeForm.patchValue({ end: start }, { emitEvent: false });
      this.isDateRangeSelected = true;
      this.onDateChanged({ startDate: start, endDate: start });
    } else if (start && end) {
      this.isDateRangeSelected = true;
      this.onDateChanged({ startDate: start, endDate: end });
    } else {
      this.isDateRangeSelected = false;
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the selected date range and applies filters.
   * @param {{ startDate: Date; endDate: Date }} range - The selected date range.
   * @side-effect Updates `selected` and calls `onFiltersApplied`.
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
    this.onFiltersApplied(); // reuse your logic
  }

  /**
   * @method clearDateRange
   * @description Resets the date range filter.
   * @side-effect Resets `dateRangeForm`, `isDateRangeSelected`, `selected`, calls `onFiltersApplied`.
   */
  public clearDateRange(): void {
    this.dateRangeForm.reset();
    this.isDateRangeSelected = false;
    this.selected = undefined;
    this.onFiltersApplied();
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `requestSubmissionFrom`.
   */
  public get f() {
    return this.requestSubmissionFrom.controls;
  }

  /**
   * @method onResize
   * @description Host listener for window resize events. Adjusts `isWebViewOptionsClicked` based on window width.
   * @lifecycle HostListener
   */
  @HostListener('window:resize', ['$event'])
  onResize() {
    this.isWebViewOptionsClicked = window.innerWidth < 525 ? true : false;
  }

  /**
   * @method toggleAggregatesCollapse
   * @description Toggles the expanded/collapsed state of the aggregates section.
   * @side-effect Updates `isAggregatesExpanded`.
   */
  public toggleAggregatesCollapse(): void {
    this.isAggregatesExpanded = !this.isAggregatesExpanded; // Toggle the expanded state
  }

  /**
   * @method toggleCReportsCollapse
   * @description Toggles the expanded/collapsed state of the reports section.
   * @side-effect Updates `isReportsExpanded`.
   */
  public toggleCReportsCollapse(): void {
    this.isReportsExpanded = !this.isReportsExpanded
  }

  /**
   * @method initialDataOnPageLoad
   * @async
   * @description Fetches initial data required for the page.
   * - Loads provider managers and technicians based on user role.
   * - Fetches visit statistics.
   * @side-effect Updates `initialPageLoader`, `$providerManagersListCpy`, `$providerTechniciansListCpy`, `providerVisitsStatsOriginal`.
   * @throws {Error} Displays toast error if data fetching fails.
   */
  public async initialDataOnPageLoad() {
    this.initialPageLoader = true;
    try {
      // To get provider business persons list
      const businessPersonsList =
        await this.personsService.getPersonsbyBusinessID(USER_SESSION_SERVICE.currentBusinessId);
      if (USER_SESSION_SERVICE.currentUserRole === PERSON_ROLE_KEYS.BUSINESS_ADMIN) {
        this.$providerManagersListCpy = (businessPersonsList ?? []).filter(
          (person) => person.level == 2
        );
        this.$providerTechniciansListCpy = (businessPersonsList ?? []).filter(
          (person) => person.level == 3
        );
      } else {
        this.$providerTechniciansListCpy = (businessPersonsList ?? []).filter(
          (person) =>
            person.level == 3 && person.reportingTo.id == USER_SESSION_SERVICE.currentUserId
        );
      }
      // To get Stats count by Business level and manager level
      if (USER_SESSION_SERVICE.currentUserRole == PERSON_ROLE_KEYS.BUSINESS_ADMIN) {
        this.providerVisitsStatsOriginal =
          await this.providerVisitsStatsService.getVisitsStatsByBusinessId(
            this.$providerManagersListCpy.map((obj) => obj.id).join(","),
            this.$providerTechniciansListCpy.map((obj) => obj.id).join(","),
            'all'
          );
        this.providerVisitsStats =
          this.providerVisitsStatsOriginal?._businessAggregate[0];
      } else {
        this.providerVisitsStatsOriginal =
          await this.providerVisitsStatsService.getVisitsStatsByManagerId(
            this.$providerTechniciansListCpy.map((obj) => obj.id).join(","),
            'all'
          );
        this.providerVisitsStats =
          this.providerVisitsStatsOriginal?._managerAggregates[0];
      }
      this.selectedManagerObj = '';
      this.selectedTechnicianObj = '';
    } catch (error) {
      this.toasterService.clear();
      this.toasterService.error(
        "Something went wrong try again after some time",
        "",
        {
          timeOut: 2000,
        }
      );
      this.initialPageLoader = false;
    }
    this.initialPageLoader = false;
  }

  /**
   * @method updateStatsReportsDataOnActions
   * @description Updates the displayed statistics and report data based on manager/technician selection.
   * Filters the stats based on the current selection state (Manager only, Technician only, Both, or None).
   * @side-effect Updates `providerVisitsStats`, calls `updateVisitsDataOnAction` and `exploreReportView` if applicable.
   */
  public updateStatsReportsDataOnActions() {
    this.providerVisitsStats = {};
    const selectionState = `${this.isManagerSelected}-${this.isTechnicianSelected}`;

    switch (selectionState) {
      case "true-true":
      case "false-true":
        // Both manager and technician selected or Only technician selected
        this.providerVisitsStats =
          this.providerVisitsStatsOriginal?._technicianAggregates?.find(
            (technician) =>
              technician?.technician_id == this.selectedTechnicianObj.id
          );
        break;
      case "true-false":
        // Only manager selected
        this.providerVisitsStats =
          this.providerVisitsStatsOriginal?._managerAggregates?.find(
            (manager) => manager?.manager_id == this.selectedManagerObj.id
          );
        break;
      case "false-false":
        // Neither selected
        this.providerVisitsStats =
          USER_SESSION_SERVICE.currentUserRole == PERSON_ROLE_KEYS.BUSINESS_ADMIN
            ? this.providerVisitsStatsOriginal?._businessAggregate[0]
            : this.providerVisitsStatsOriginal?._managerAggregates[0];
        break;
    }
    this.updateVisitsDataOnAction();
    if (this.isDealerVisitReport) {
      this.exploreReportView('dealer_orders');
    } else if (this.isDealerPaymentReport) {
      this.exploreReportView('dealer_payments');
    } else if (this.isFarmerVisitReport) {
      this.exploreReportView('farmer_visits');
    } else if (this.isConsultantVisitReport) {
      this.exploreReportView('consultant_visits');
    } else if (this.isHarvestSummaryReport) {
      this.exploreReportView('harvest_summary');
    }
  }

  /**
   * @method updateVisitsDataOnAction
   * @description Updates the visits list and location tracking details based on manager/technician selection.
   * Filters the data based on the current selection state.
   * @side-effect Updates `providerVisitsListCpy`, `locationTrackingDetailsCpy`, calls `onFiltersApply`.
   */
  public updateVisitsDataOnAction() {
    this.providerVisitsList = [];
    const selectionState = `${this.isManagerSelected}-${this.isTechnicianSelected}`;

    switch (selectionState) {
      case "true-true":
      case "false-true":
        // Both manager and technician selected or Only technician selected

        // visit details
        this.providerVisitsListCpy = this.providerVisitsListOriginal?.filter(
          (visit: ProviderVisitsModel) =>
            visit?.visiting_person_id == this.selectedTechnicianObj.id
        );
        // Location Tracking Details
        this.locationTrackingDetailsCpy =
          this.locationTrackingDetailsOriginal?.filter(
            (tracking) => tracking?.personID?.id == this.selectedTechnicianObj.id
          );
        break;
      case "true-false":
        // Only manager selected

        // visit details
        const technicianIdsAssociatedToSelectedManager =
          this.$providerTechniciansList?.map(
            (person: PersonsModel) => person.id
          );
        this.providerVisitsListCpy = this.providerVisitsListOriginal?.filter(
          (visit: ProviderVisitsModel) =>
            technicianIdsAssociatedToSelectedManager?.includes(
              visit.visiting_person_id
            )
        );
        // Location Tracking Details
        this.locationTrackingDetailsCpy =
          this.locationTrackingDetailsOriginal?.filter(
            (tracking) =>
              tracking?.personID?.reportingTo == this.selectedManagerObj.id
          );
        break;
      case "false-false":
        // Neither selected

        // Visit details
        if (USER_SESSION_SERVICE.currentUserRole == PERSON_ROLE_KEYS.PROVIDER_MANAGER) {
          const technicianIdsAssociatedToSelectedManager =
            this.$providerTechniciansListCpy?.map(
              (person: PersonsModel) => person.id
            );
          this.providerVisitsListCpy = this.providerVisitsListOriginal?.filter(
            (visit: ProviderVisitsModel) =>
              technicianIdsAssociatedToSelectedManager?.includes(
                visit.visiting_person_id
              )
          );
          // Location Tracking Details
          this.locationTrackingDetailsCpy =
            this.locationTrackingDetailsOriginal?.filter(
              (tracking) =>
                tracking?.personID?.reportingTo == USER_SESSION_SERVICE.currentUserId
            );
        } else {
          this.providerVisitsListCpy = this.providerVisitsListOriginal;
          // Location Tracking Details
          this.locationTrackingDetailsCpy = this.locationTrackingDetailsOriginal;
        }
        break;
    }
    this.onFiltersApply();
    this.ngAfterViewInit();
  }

  /**
   * @method toogleVisitsViewState
   * @async
   * @description Toggles between tabular view and map view.
   * Loads data lazily if not already loaded. Initiates parallel image processing for visits.
   * @param {string} viewType - The type of view to switch to ('tabularView' or 'mapView').
   * @side-effect Updates view flags, loaders, fetches data, calls `processImagesInParallel`.
   */
  public async toogleVisitsViewState(viewType: string) {
    this.isWebViewOptionsClicked = true;

    if (viewType === 'tabularView') {
      this.isTabularView = true;
      this.isMapView = false;
      this.tabularViewLoader = true;

      try {
        if (!this.providerVisitsListOriginal?.length) {
          const visits = await this.providerVisitsService.getVisitsByProviderBusinessId(USER_SESSION_SERVICE.currentBusinessId);

          this.providerVisitsListOriginal = visits.map(v => ({
            ...v,
            isImageLoading: true,
            imageBase64String: ''
          }));

          // Process images in background, DO NOT block other flows
          setTimeout(() => {
            this.processImagesInParallel(this.providerVisitsListOriginal, 3); // 3 concurrent
          });

          this.providerVisitsList = this.providerVisitsListCpy = this.providerVisitsListOriginal;
        }
      } catch (error) {
        this.toasterService.clear();
        this.toasterService.error("Something went wrong try again after some time", '', { timeOut: 2000 });
      } finally {
        this.tabularViewLoader = false;
        this.updateVisitsDataOnAction();
      }
    } else {
      this.isMapView = true;
      this.isTabularView = false;
      this.mapsViewLoader = true;

      try {
        if (!this.locationTrackingDetailsOriginal?.length) {
          this.locationTrackingDetails = this.locationTrackingDetailsCpy = this.locationTrackingDetailsOriginal =
            await this.locationTrackerService.getProviderVisitTrackingDetailsByBusinessLevel();
        }

        if (!this.providerVisitsListOriginal?.length) {
          const visits = await this.providerVisitsService.getVisitsByProviderBusinessId(USER_SESSION_SERVICE.currentBusinessId);

          this.providerVisitsListOriginal = visits.map(v => ({
            ...v,
            isImageLoading: true,
            imageBase64String: ''
          }));

          // Process images in background, DO NOT block other flows
          setTimeout(() => {
            this.processImagesInParallel(this.providerVisitsListOriginal, 3); // 3 concurrent
          });

          this.providerVisitsList = this.providerVisitsListCpy = this.providerVisitsListOriginal;
        }
      } catch (error) {
        this.toasterService.clear();
        this.toasterService.error("Something went wrong try again after some time", '', { timeOut: 2000 });
      } finally {
        this.mapsViewLoader = false;
        this.updateVisitsDataOnAction();
        this.ngAfterViewInit();
      }
    }
  }

  /**
   * @method processImagesInParallel
   * @private
   * @description Processes visit images in parallel batches to avoid blocking the UI.
   * @param {any[]} visits - List of visits to process.
   * @param {number} concurrencyLimit - Number of concurrent downloads allowed. Default 3.
   */
  private processImagesInParallel(visits: any[], concurrencyLimit = 3) {
    let index = 0;
    const queue: Promise<any>[] = [];

    const runNext = () => {
      if (index >= visits.length) return;

      const visit = visits[index++];
      const promise = this.downloadVisitImage(visit).finally(runNext);
      queue.push(promise);
    };

    // Start N concurrent tasks
    for (let i = 0; i < concurrencyLimit; i++) runNext();
  }

  /**
   * @method downloadVisitImage
   * @private
   * @description Downloads a single visit image from S3 and converts it to Base64.
   * @param {any} visit - The visit object containing attachment path.
   * @returns {Promise<void>}
   */
  private downloadVisitImage(visit: any): Promise<void> {
    return new Promise((resolve) => {
      if (!visit.attachment_path || visit.attachment_path === 'null') {
        visit.imageBase64String = '';
        visit.isImageLoading = false;
        return resolve();
      }

      this.businessEntityService.downloadFileFromS3Bucket(visit.attachment_path)
        .pipe(timeout(7000)) // 7s timeout per image
        .subscribe({
          next: (fileBlob) => {
            const ext = (visit.attachment_path || '').split('.').pop()?.toLowerCase();
            let contentType = 'image/jpeg';
            if (ext === 'png') contentType = 'image/png';
            else if (ext === 'webp') contentType = 'image/webp';

            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(',')[1];
              visit.imageBase64String = `data:${contentType};base64,${base64}`;
              visit.isImageLoading = false;
              resolve();
            };
            reader.onerror = () => {
              visit.imageBase64String = '';
              visit.isImageLoading = false;
              resolve();
            };
            reader.readAsDataURL(fileBlob);
          },
          error: () => {
            visit.imageBase64String = '';
            visit.isImageLoading = false;
            resolve();
          }
        });
    });
  }

  /**
   * @method onManagerSelectionChange
   * @description Handles manager selection change from dropdown.
   * Filters technicians list based on selected manager.
   * @param {any} value - Selected manager object.
   */
  public onManagerSelectionChange(value: any) {
    this.isManagerSelected = !!value;
    this.selectedManagerObj = value || '';
    this.managerDropdownOpen = false;
    this.selectedTechnicianObj = '';
    this.isTechnicianSelected = false;
    this.$providerTechniciansList = this.$providerTechniciansListCpy.filter(
      (person) => person.reportingTo.id == value.id
    );
    this.updateStatsReportsDataOnActions();
  }

  /**
   * @method onTechnicianSelectionChange
   * @description Handles technician selection change from dropdown.
   * @param {any} value - Selected technician object.
   */
  public onTechnicianSelectionChange(value: any) {
    this.isTechnicianSelected = !!value;
    this.selectedTechnicianObj = value || '';
    this.technicianDropdownOpen = false;
    this.updateStatsReportsDataOnActions();
  }

  /**
   * @method onCustomerTypeSelectionChange
   * @description Handles customer type filter change.
   * @param {string} value - Selected customer type.
   */
  public onCustomerTypeSelectionChange(value: string): void {
    this.isTypeofCustomerSelected = !!value;
    this.typeOfCustomer = value || '';     // '' or a real value
    this.onFiltersApplied();
  }

  /**
   * @method onVisitorSelectionChange
   * @description Handles visitor name filter change.
   * @param {string} value - Selected visitor name.
   */
  public onVisitorSelectionChange(value: string): void {
    this.isNameSelected = !!value;
    this.visitorName = value || '';     // '' or a real value
    this.onFiltersApplied();
  }

  /**
   * @method onTechniciansSelectionChange
   * @description Handles technician name filter change (for reports).
   * @param {string} value - Selected technician name.
   */
  public onTechniciansSelectionChange(value: string): void {
    this.isAssociationTechnicianSelected = !!value;
    this.technicianName = value || '';     // '' or a real value
    this.onFiltersApplied();
  }

  /**
   * @method onLocationSelectionChange
   * @description Handles location filter change.
   * @param {string} value - Selected location name.
   */
  public onLocationSelectionChange(value: string): void {
    this.isLocationSelected = !!value;
    this.locationName = value || '';     // '' or a real value
    this.onFiltersApplied();
  }

  /**
   * @method onBusinessSelectionChange
   * @description Handles farm business name filter change.
   * @param {string} value - Selected business name.
   */
  public onBusinessSelectionChange(value: string): void {
    this.isFarmBusinessNameSelected = !!value;
    this.farmBusinessName = value || '';     // '' or a real value
    this.onFiltersApplied();
  }

  /**
   * @method getVisitDetailsByVisitId
   * @async
   * @description Fetches detailed information for a specific visit, including activities, product placements, usage, returns, and movements.
   * Opens the visit details modal.
   * @param {ProviderVisitsModel} visitObject - The visit object to fetch details for.
   * @side-effect Updates `visitObjectDetails`, opens modal, updates `visitDetailsLoader`.
   */
  public async getVisitDetailsByVisitId(visitObject: ProviderVisitsModel) {
    this.submitted = false;
    this.visitObject = visitObject;
    this.visitComment = "";
    this.visitDetailsLoader = true;
    const modal: HTMLElement = document.getElementById('custom-modal') as HTMLElement;
    modal.classList.add('show'); // Add the class with !important
    try {
      this.visitObjectDetails = await this.providerVisitsService.getVisitDetailsByVisitId(visitObject.id);
      this.visitObjectDetails[0].attachment_path = visitObject.attachment_path;

      const productPlacementsDetails: any =
        await this.productPlacementsService.getProductPlacementDeatilsById(visitObject.id);

      this.visitObjectDetails[0]["productReturns"] = this.visitObjectDetails[0].ProductMovementsReturns?.filter(obj => obj.activity_id == 11) || [];
      this.visitObjectDetails[0]["productMovements"] = this.visitObjectDetails[0].ProductMovementsReturns?.filter(obj => obj.activity_id == 10) || [];
      this.visitObjectDetails[0]["productUsages"] = this.visitObjectDetails[0].ProductUsagesDetails?.filter(obj => obj.activity_id == 13) || [];
      this.visitObjectDetails[0]["productDiscussed"] = this.visitObjectDetails[0].ProductUsagesDetails?.filter(obj => obj.activity_id == 14 || obj.activity_id == 15) || [];
      this.visitObjectDetails[0]["productPlacements"] = productPlacementsDetails?.[0]?.ProductPlacementDetailsModel || [];
    } catch (error) {
      this.toasterService.clear();
      this.toasterService.error("Something went wrong try again", "", { timeOut: 2000 });
      this.visitDetailsLoader = false;
    }
    let activitiesCount: number = 0;
    this.visitDetailsLoader = false;
    if (this.visitObjectDetails[0]["productPlacements"].length) {
      this.showHideVisitDetailsContentBlocks('Placements');
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["productMovements"].length) {
      this.showHideVisitDetailsContentBlocks('Movements');
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["productReturns"].length) {
      this.showHideVisitDetailsContentBlocks('Returns');
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["ProviderVisitPayments"].length) {
      this.showHideVisitDetailsContentBlocks('Payments');
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["ProviderVisitSurveys"].length) {
      this.showHideVisitDetailsContentBlocks('harvestSummary');
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["productUsages"].length) {
      this.showHideVisitDetailsContentBlocks('ListOfProductsUsed');
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["productDiscussed"].length) {
      activitiesCount++;
    }
    if (this.visitObjectDetails[0]["productPlacements"].length) {
      this.showHideVisitDetailsContentBlocks('Placements');
    } else if (this.visitObjectDetails[0]["productMovements"].length) {
      this.showHideVisitDetailsContentBlocks('Movements');
    } else if (this.visitObjectDetails[0]["productReturns"].length) {
      this.showHideVisitDetailsContentBlocks('Returns');
    } else if (this.visitObjectDetails[0]["ProviderVisitPayments"].length) {
      this.showHideVisitDetailsContentBlocks('Payments');
    }
    this.visitObjectDetails[0]["activitiesCount"] = activitiesCount;
  }

  /**
   * @method updateVisitByVisitId
   * @async
   * @description Updates the status of a visit (Accept/Reject) with an optional comment.
   * @param {string} visitStatus - The new status ('A' for Accept, 'R' for Reject).
   * @side-effect Calls API to update visit, shows toast notification.
   */
  public async updateVisitByVisitId(visitStatus: string) {
    try {
      this.submitted = true;

      // stop here if form is invalid
      if (this.requestSubmissionFrom.invalid) {
        return;
      }

      this.isApprovalButtonClicked = true;
      const isConfirmed = confirm(`Are you sure you want to ${visitStatus == "A" ? "accept" : "reject"} this visit`);
      if (isConfirmed) {
        this.visitObject.status = visitStatus;
        if (this.visitComment) this.visitObject.remarks = this.visitComment;

        const visitObject = cloneDeep(this.visitObject);
        delete visitObject['imageBase64String'];
        const isVisitUpdated = await this.providerVisitsService.updateOneByID(
          visitObject.id,
          visitObject
        );
        if (isVisitUpdated) {
          this.toasterService.success(
            `Visit ${visitStatus == "A" ? "Accepted" : "Rejected"} sucessfully`,
            "",
            {
              timeOut: 4000,
            }
          );
        }
        this.selectedVisitStatus = 'P';
      }
    } catch (error) {
      this.toasterService.clear();
      this.toasterService.error("Something went wrong try again", "", {
        timeOut: 2000,
      });
      this.isApprovalButtonClicked = false;
    }
  }

  /**
   * @method closePopup
   * @description Closes the visit details modal popup.
   * @side-effect Removes 'show' class from modal, calls `updateVisitsDataOnAction`.
   */
  public closePopup() {
    const modal: HTMLElement = document.getElementById('custom-modal') as HTMLElement;
    modal.classList.remove('show'); // Add the class with !important
    this.updateVisitsDataOnAction();
  }

  /**
   * @method exploreReportView
   * @async
   * @description Handles the logic for exploring different report types.
   * Fetches report data based on the selected report name and current filters.
   * @param {string} reportName - The name of the report to explore (e.g., 'dealer_orders').
   * @side-effect Updates report flags, fetches data, updates report lists.
   */
  public async exploreReportView(reportName: string) {
    try {
      this.hideExploreReportView();
      this.isReportViewClicked = true;
      this.reportsLoader = true;
      // Reset all report flags
      this.resetReportFlags();

      const reportMap: { [key: string]: { flag: string; data: string } } = {
        dealer_orders: { flag: 'isDealerVisitReport', data: 'dealervisits' },
        dealer_payments: { flag: 'isDealerPaymentReport', data: 'dealerpayments' },
        farmer_visits: { flag: 'isFarmerVisitReport', data: 'farmerVisits' },
        consultant_visits: { flag: 'isConsultantVisitReport', data: 'consultantVisits' },
        harvest_summary: { flag: 'isHarvestSummaryReport', data: 'harvestSummaries' },
      };

      const reportConfig = reportMap[reportName];
      if (reportConfig) {
        this[reportConfig.flag] = true;
        const fetchedData = await this.providerVisitsService.getVisitReports(this.getExcelReportReqBody());
        this[`${reportConfig.data}Cpy`] = fetchedData;
        this[reportConfig.data] = fetchedData;
      }

      this.searchBy = this.isDealerVisitReport || this.isConsultantVisitReport
        ? 'Search by product'
        : this.isDealerPaymentReport
          ? 'Search by dealer name'
          : this.isFarmerVisitReport
            ? 'Search by product, dealer'
            : this.isHarvestSummaryReport
              ? 'Search by type, business'
              : '';

    } catch (error) {
      this.toasterService.clear();
      this.toasterService.error("Error while fetching report data:", "", {
        timeOut: 2000,
      });
      this.isErrorOccured = true;
    } finally {
      this.reportsLoader = false;
    }
  }

  /**
   * @method resetReportFlags
   * @private
   * @description Resets all report type flags to false.
   */
  private resetReportFlags() {
    this.isDealerVisitReport = false;
    this.isDealerPaymentReport = false;
    this.isFarmerVisitReport = false;
    this.isConsultantVisitReport = false;
    this.isHarvestSummaryReport = false;
  }

  /**
   * @method getBusinessesFromResponse
   * @description Extracts unique business names from the current report data.
   * @returns {string[]} Array of unique business names.
   */
  public getBusinessesFromResponse() {
    switch (true) {
      case this.isDealerVisitReport:
        if (this.dealervisitsCpy.length) {
          return [...new Set(this.dealervisitsCpy.map((visit: any) => visit.visited_business_name.trim()))]
        }
        break;
      case this.isDealerPaymentReport:
        if (this.dealerpaymentsCpy.length) {
          return [...new Set(this.dealerpaymentsCpy.map((visit: any) => visit.visited_business_name.trim()))]
        }
        break;
      case this.isFarmerVisitReport:
        if (this.farmerVisitsCpy.length) {
          return [...new Set(this.farmerVisitsCpy.map((visit: any) => visit.farmer_name.trim()))]
        }
        break;
      case this.isConsultantVisitReport:
        if (this.consultantVisitsCpy.length) {
          return [...new Set(this.consultantVisitsCpy.map((visit: any) => visit.consultant_name.trim()))]
        }
        break;
      case this.isHarvestSummaryReport:
        if (this.harvestSummariesCpy.length) {
          return [...new Set(this.harvestSummariesCpy.map((visit: any) => visit.farmer_name.trim()))]
        }
        break;
      default:
        break;
    }
  }

  /**
   * @method getTechniciansFromResponse
   * @description Extracts unique technician names from the current report data.
   * @returns {string[]} Array of unique technician names.
   */
  public getTechniciansFromResponse() {
    switch (true) {
      case this.isDealerVisitReport:
        if (this.dealervisitsCpy.length) {
          return [...new Set(this.dealervisitsCpy.map((visit: any) => visit.visiting_person_name))]
        }
        break;
      case this.isDealerPaymentReport:
        if (this.dealerpaymentsCpy.length) {
          return [...new Set(this.dealerpaymentsCpy.map((visit: any) => visit.visiting_person_name))]
        }
        break;
      case this.isFarmerVisitReport:
        if (this.farmerVisitsCpy.length) {
          return [...new Set(this.farmerVisitsCpy.map((visit: any) => visit.visiting_person_name))]
        }
        break;
      case this.isConsultantVisitReport:
        if (this.consultantVisitsCpy.length) {
          return [...new Set(this.consultantVisitsCpy.map((visit: any) => visit.visiting_person_name))]
        }
        break;
      default:
        break;
    }
  }

  /**
   * @method getLocationsFromResponse
   * @description Extracts unique location names from the current report data.
   * @returns {string[]} Array of unique location names.
   */
  public getLocationsFromResponse() {
    switch (true) {
      case this.isDealerVisitReport:
        if (this.dealervisitsCpy.length) {
          return [...new Set(this.dealervisitsCpy.map((visit: any) => visit.site_name))]
        }
        break;
      case this.isDealerPaymentReport:
        if (this.dealerpaymentsCpy.length) {
          return [...new Set(this.dealerpaymentsCpy.map((visit: any) => visit.site_name))]
        }
        break;
      case this.isFarmerVisitReport:
        if (this.farmerVisitsCpy.length) {
          return [...new Set(this.farmerVisitsCpy.map((visit: any) => visit.site_name))]
        }
        break;
      case this.isConsultantVisitReport:
        if (this.consultantVisitsCpy.length) {
          return [...new Set(this.consultantVisitsCpy.map((visit: any) => visit.site_name))]
        }
        break;
      case this.isHarvestSummaryReport:
        if (this.harvestSummariesCpy.length) {
          return [...new Set(this.harvestSummariesCpy.map((visit: any) => visit.site_name))]
        }
        break;
      default:
        break;
    }
  }

  /**
   * @method getFarmBusinessFromResponse
   * @description Extracts unique farm business names from harvest summary report data.
   * @returns {string[]} Array of unique farm business names.
   */
  public getFarmBusinessFromResponse() {
    return [...new Set(this.harvestSummariesCpy.map((visit: any) => visit.farm_name))]
  }

  /**
   * @method getCustomerTypeFromResponse
   * @description Extracts unique customer types from harvest summary report data.
   * @returns {string[]} Array of unique customer types.
   */
  public getCustomerTypeFromResponse() {
    return [...new Set(this.harvestSummariesCpy.map((visit: any) => visit.customer_type))]
  }

  /**
   * @method hideExploreReportView
   * @description Resets the report view state, clearing all report data and filters.
   * @side-effect Resets flags, clears data arrays, clears filters.
   */
  public hideExploreReportView() {
    this.reportsLoader = false;
    this.isErrorOccured = false;
    this.isReportViewClicked = false;
    this.isDealerVisitReport = false;
    this.isDealerPaymentReport = false;
    this.isFarmerVisitReport = false;
    this.isConsultantVisitReport = false;
    this.isHarvestSummaryReport = false;
    this.visitorName = '';
    this.technicianName = '';
    this.locationName = '';
    this.farmBusinessName = '';
    this.typeOfCustomer = '';
    this.isNameSelected = false;
    this.isLocationSelected = false;
    this.isAssociationTechnicianSelected = false;
    this.isFarmBusinessNameSelected = false;
    this.isTypeofCustomerSelected = false;
    this.isDateRangeSelected = false;
    this.clearDateRange();
    this.dealervisitsCpy = this.dealerpaymentsCpy = this.farmerVisitsCpy = this.consultantVisitsCpy = this.harvestSummariesCpy = [];
    this.dealervisits = this.dealerpayments = this.farmerVisits = this.consultantVisits = this.harvestSummaries = []
  }

  /**
   * @method onFiltersApplied
   * @description Applies selected filters to the current report data.
   * @side-effect Updates displayed report lists based on filters.
   */
  public onFiltersApplied() {
    if (this.isFiltersSelected()) {
      this.applyFiltersOnVisitReport();
    } else {
      this.dealervisits = this.dealervisitsCpy;
      this.dealerpayments = this.dealerpaymentsCpy;
      this.farmerVisits = this.farmerVisitsCpy;
      this.consultantVisits = this.consultantVisitsCpy;
      this.harvestSummaries = this.harvestSummariesCpy;
    }
  }

  /**
   * @method isFiltersSelected
   * @description Checks if any filter is currently selected.
   * @returns {boolean} True if any filter is active.
   */
  public isFiltersSelected() {
    return this.isNameSelected || this.isAssociationTechnicianSelected || this.isLocationSelected || this.isDateRangeSelected || this.isTypeofCustomerSelected || this.isFarmBusinessNameSelected;
  }

  /**
   * @method applyFiltersOnVisitReport
   * @private
   * @description Applies specific filtering logic based on the active report type.
   */
  private applyFiltersOnVisitReport() {

    switch (true) {
      case this.isDealerVisitReport:
        this.dealervisits = this.dealervisitsCpy.filter((visit: any) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (this.isNameSelected && this.visitorName && visit.visited_business_name.trim() != this.visitorName) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }
          if (this.isAssociationTechnicianSelected && this.technicianName && visit.visiting_person_name != this.technicianName) {
            includeConditions = false;
          }
          if (this.isLocationSelected && this.locationName && visit.site_name != this.locationName) {
            includeConditions = false;
          }
          if (this.selected && this.selected.startDate && this.selected.endDate) {
            const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
            const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
            const visitDate = new Date(visit.visit_time?.split('T')[0]);
            if (!(visitDate >= startDate && visitDate <= endDate)) {
              includeConditions = false;
            }
          }
          return includeConditions;
        });
        break;
      case this.isDealerPaymentReport:
        this.dealerpayments = this.dealerpaymentsCpy.filter((visit: any) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (this.isNameSelected && this.visitorName && visit.visited_business_name.trim() != this.visitorName) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }
          if (this.isAssociationTechnicianSelected && this.technicianName && visit.visiting_person_name != this.technicianName) {
            includeConditions = false;
          }
          if (this.isLocationSelected && this.locationName && visit.site_name != this.locationName) {
            includeConditions = false;
          }
          if (this.selected && this.selected.startDate && this.selected.endDate) {
            const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
            const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
            const visitDate = new Date(visit.date_time?.split('T')[0]);
            if (!(visitDate >= startDate && visitDate <= endDate)) {
              includeConditions = false;
            }
          }
          return includeConditions;
        });
        break;
      case this.isFarmerVisitReport:
        this.farmerVisits = this.farmerVisitsCpy.filter((visit: any) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (this.isNameSelected && this.visitorName && visit.farmer_name.trim() != this.visitorName) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }
          if (this.isAssociationTechnicianSelected && this.technicianName && visit.visiting_person_name != this.technicianName) {
            includeConditions = false;
          }
          if (this.isLocationSelected && this.locationName && visit.site_name != this.locationName) {
            includeConditions = false;
          }
          if (this.selected && this.selected.startDate && this.selected.endDate) {
            const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
            const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
            const visitDate = new Date(visit.visit_time?.split('T')[0]);
            if (!(visitDate >= startDate && visitDate <= endDate)) {
              includeConditions = false;
            }
          }
          return includeConditions;
        });
        break;
      case this.isConsultantVisitReport:
        this.consultantVisits = this.consultantVisitsCpy.filter((visit: any) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (this.isNameSelected && this.visitorName && visit.consultant_name.trim() != this.visitorName) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }
          if (this.isAssociationTechnicianSelected && this.technicianName && visit.visiting_person_name != this.technicianName) {
            includeConditions = false;
          }
          if (this.isLocationSelected && this.locationName && visit.site_name != this.locationName) {
            includeConditions = false;
          }
          if (this.selected && this.selected.startDate && this.selected.endDate) {
            const startDate = new Date(this.selected.startDate.format('YYYY-MM-DD'));
            const endDate = new Date(this.selected.endDate.format('YYYY-MM-DD'));
            const visitDate = new Date(visit.visit_time?.split('T')[0]);
            if (!(visitDate >= startDate && visitDate <= endDate)) {
              includeConditions = false;
            }
          }
          return includeConditions;
        });
        break;
      case this.isHarvestSummaryReport:
        this.harvestSummaries = this.harvestSummariesCpy.filter((visit: any) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (this.isTypeofCustomerSelected && this.typeOfCustomer && visit.customer_type != this.typeOfCustomer) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }
          if (this.isLocationSelected && this.locationName && visit.site_name != this.locationName) {
            includeConditions = false;
          }
          if (this.isNameSelected && this.visitorName && visit.farmer_name.trim() != this.visitorName) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }
          if (this.isFarmBusinessNameSelected && this.farmBusinessName && visit.farm_name != this.farmBusinessName) {
            includeConditions = false;
          }
          return includeConditions;
        });
        break;
      default:
        break;
    }
  }

  /**
   * @method isVisitsDataAvailable
   * @description Checks if there is any visit data available across all report types.
   * @returns {boolean} True if data exists.
   */
  public isVisitsDataAvailable() {
    const hasNonZeroLength =
      this.$providerTechniciansListCpy.length > 0
        ? this.dealervisitsCpy.length > 0 ||
        this.dealerpaymentsCpy.length > 0 ||
        this.farmerVisitsCpy.length > 0 ||
        this.consultantVisitsCpy.length > 0 ||
        this.harvestSummariesCpy.length > 0
        : false
    return hasNonZeroLength;
  }

  /**
   * @method getFilterLableName
   * @description Returns the label name for the filter based on the active report type.
   * @returns {string} Label name (e.g., 'Dealership', 'Farmer').
   */
  public getFilterLableName() {
    let labelName = '';
    switch (true) {
      case this.isDealerVisitReport:
      case this.isDealerPaymentReport:
        labelName = 'Dealership';
        break;
      case this.isFarmerVisitReport:
        labelName = 'Farmer'
        break;
      case this.isConsultantVisitReport:
        labelName = 'Consultant'
        break;
      case this.isHarvestSummaryReport:
        labelName = 'Farmer'
        break;
    }
    return labelName;
  }

  /**
   * @method getExcelReportReqBody
   * @description Constructs the request body for Excel report generation based on current filters and selection.
   * @returns {any} Request body object.
   */
  public getExcelReportReqBody() {
    const fileName = this.isDealerVisitReport
      ? 'dealer_placements'
      : this.isDealerPaymentReport
        ? 'dealer_payments'
        : this.isFarmerVisitReport
          ? 'farmer_usage'
          : this.isConsultantVisitReport
            ? 'sme_feedback'
            : 'harvest_summary';
    let reqBody: any;
    if (USER_SESSION_SERVICE.currentUserRole == PERSON_ROLE_KEYS.BUSINESS_ADMIN) {
      reqBody = this.isManagerSelected
        ? this.isTechnicianSelected
          ? { lookoutReport: fileName, visiting_person_id: [this.selectedTechnicianObj.id] }
          : {
            lookoutReport: fileName,
            visiting_person_id: this.$providerTechniciansList.map(
              (obj) => obj.id
            ),
          }
        : this.isTechnicianSelected
          ? { lookoutReport: fileName, visiting_person_id: [this.selectedTechnicianObj.id] }
          : { lookoutReport: fileName };
      reqBody['visited_business_type'] =
        this.isFarmerVisitReport
          ? 'farm'
          : this.isConsultantVisitReport
            ? 'sme'
            : this.isDealerVisitReport || this.isDealerPaymentReport
              ? 'dealer'
              : 'farm'
    } else {
      reqBody = this.isTechnicianSelected
        ? { lookoutReport: fileName, visiting_person_id: [this.selectedTechnicianObj.id] }
        : {
          lookoutReport: fileName,
          visiting_person_id: this.$providerTechniciansListCpy?.map(
            (obj) => obj.id
          ) || []
        }
      reqBody['visited_business_type'] =
        this.isFarmerVisitReport
          ? 'farm'
          : this.isConsultantVisitReport
            ? 'sme'
            : this.isDealerVisitReport || this.isDealerPaymentReport
              ? 'dealer'
              : 'farm'
    }

    if (this.isFiltersSelected()) {
      if (this.isNameSelected) {
        if (this.isDealerVisitReport || this.isDealerPaymentReport) {
          reqBody['visited_business_name'] = this.visitorName
        } else if (this.isFarmerVisitReport || this.isConsultantVisitReport) {
          reqBody['visited_person_name'] = this.visitorName
        }
      }
      if (this.isAssociationTechnicianSelected) {
        reqBody['visiting_person_name'] = this.technicianName
      }
      if (this.isLocationSelected && !this.isHarvestSummaryReport) {
        reqBody['site_name'] = this.locationName
      }
      if (this.isDateRangeSelected) {
        reqBody['from_date'] = moment(this.selected?.startDate).format('DD-MMM-YYYY') // sample format 13-02-2025
        reqBody['to_date'] = moment(this.selected?.endDate).format('DD-MMM-YYYY') // sample format 13-02-2025
      }
      if (this.isHarvestSummaryReport) {
        reqBody['surveyQuery'] = {};
        if (this.isLocationSelected) {
          reqBody.surveyQuery['farm_site_name'] = this.locationName;
        }
        if (this.isNameSelected) {
          reqBody.surveyQuery['farmer_name'] = this.visitorName;
        }
        if (this.isFarmBusinessNameSelected) {
          reqBody.surveyQuery['farm_name'] = this.farmBusinessName;
        }
        if (this.isTypeofCustomerSelected) {
          reqBody.surveyQuery['farm_type'] = this.typeOfCustomer;
        }
      }
    }
    return reqBody;
  }

  /**
   * @method exportReports
   * @async
   * @description Exports the current report data to an Excel file.
   * @side-effect Triggers file download.
   */
  public async exportReports() {
    const fileName = this.isDealerVisitReport
      ? 'dealer_placements'
      : this.isDealerPaymentReport
        ? 'dealer_payments'
        : this.isFarmerVisitReport
          ? 'farmer_usage'
          : this.isConsultantVisitReport
            ? 'sme_feedback'
            : 'harvest_summary';
    try {
      const fileObj = this.aquService.excelReportsDownload(
        fileName,
        this.getExcelReportReqBody()
      );
      fileObj.subscribe((fileblob) => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, fileName);
        }
      });
    } catch (error) {
      this.toasterService.clear();
      this.toasterService.error(error);
    }
  }

  /**
   * @method applyInitialDefaultFilters
   * @description Resets filters to their default state (Today, Pending status).
   * @side-effect Updates filter variables.
   */
  public applyInitialDefaultFilters() {
    this.search = "";
    this.selectedDateFilter = "Today";
    this.selectedVisitStatus = "P";
    this.isDateFilterSelected = true;
    this.isStatusFilterSelected = true;
    this.isManagerSelected = false;
    this.isTechnicianSelected = false;
  }

  /**
   * @method setDateFilter
   * @description Sets the date filter type and updates visits data.
   * @param {string} filter - Filter type (e.g., 'Today', '1W').
   * @side-effect Updates `selectedDateFilter`, calls `updateVisitsDataOnAction`.
   */
  public setDateFilter(filter: string) {
    this.customFromDate = '';
    this.customToDate = '';
    this.selectedDateFilter = filter;
    this.isDateFilterSelected = true;
    this.updateVisitsDataOnAction();
  }

  /**
   * @method setStatusFilter
   * @description Sets the visit status filter and updates visits data.
   * @param {string} filter - Status filter (e.g., 'P', 'A').
   * @side-effect Updates `selectedVisitStatus`, calls `updateVisitsDataOnAction`.
   */
  public setStatusFilter(filter: string) {
    this.selectedVisitStatus = filter;
    this.isStatusFilterSelected = true;
    this.updateVisitsDataOnAction();
  }

  /**
   * @method openDateRangePopup
   * @description Opens the custom date range popup.
   */
  openDateRangePopup() {
    this.isDateRangePopupOpen = true;
  }

  /**
   * @method closeDateRangePopup
   * @description Closes the custom date range popup.
   */
  closeDateRangePopup() {
    this.isDateRangePopupOpen = false;
  }

  /**
   * @method onDateRangeApply
   * @description Applies the custom date range filter.
   * @param {object} event - Object containing `fromDate` and `toDate`.
   * @side-effect Updates custom dates, sets filter to 'Custom', calls `updateVisitsDataOnAction`.
   */
  onDateRangeApply(event: { fromDate: string; toDate: string }) {
    this.customFromDate = event.fromDate;
    this.customToDate = event.toDate;
    this.selectedDateFilter = 'Custom';
    this.isDateFilterSelected = true;
    this.updateVisitsDataOnAction();
  }

  /**
   * @method onFiltersApply
   * @description Applies search and other filters to the visits list.
   * @side-effect Updates `providerVisitsList`, `locationTrackingDetails`.
   */
  public onFiltersApply() {
    this.resetMapState();
    const value = this.search?.replace(/[-_ :.]/g, "").toLowerCase();
    if (this.search != "") {
      if (this.isStatusFilterSelected || this.isDateFilterSelected) {
        this.applySearchWithActionsFilter(value);
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.isStatusFilterSelected || this.isDateFilterSelected) {
        this.applySearchWithActionsFilter(value);
      } else {
        this.providerVisitsList = this.providerVisitsListCpy;
        this.locationTrackingDetails = this.locationTrackingDetailsCpy;
      }
    }
  }

  /**
   * @method applySearchWithActionsFilter
   * @description Applies search, status, and date filters combined.
   * @param {string} searchValue - Normalized search string.
   * @side-effect Updates `providerVisitsList` or `locationTrackingDetails`.
   */
  public applySearchWithActionsFilter(searchValue: string) {
    if (this.isTabularView) {
      this.providerVisitsList = this.providerVisitsListCpy?.filter(
        (visit: ProviderVisitsModel) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (
            this.isStatusFilterSelected &&
            visit.status != this.selectedVisitStatus
          ) {
            includeConditions = false; // Set shouldKeep to false if condition is not met
          }

          if (this.isDateFilterSelected) {
            let startDate: Date;
            let endDate: Date;
            const todaysDate = new Date();

            switch (this.selectedDateFilter) {
              case "Today":
                // Start and End date for Today
                startDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  todaysDate.getDate(),
                  0,
                  0,
                  0,
                  0
                ); // Midnight today
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  todaysDate.getDate(),
                  23,
                  59,
                  59,
                  999
                ); // End of today
                break;

              case "1W":
                // Last week: Start date 7 days ago from yesterday, End date yesterday
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  todaysDate.getDate() - 1,
                  23,
                  59,
                  59,
                  999
                ); // Yesterday
                startDate = new Date(
                  endDate.getFullYear(),
                  endDate.getMonth(),
                  endDate.getDate() - 6,
                  0,
                  0,
                  0,
                  0
                ); // 7 days before yesterday
                break;

              case "1M":
                // Last full month: Start date is the 1st day of the previous month, End date is the last day of the previous month
                startDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth() - 1,
                  1,
                  0,
                  0,
                  0,
                  0
                ); // 1st day of the last month
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  0,
                  23,
                  59,
                  59,
                  999
                ); // Last day of the last month
                break;

              case "6M":
                // Last 6 months: Start date is the 1st day of the month 6 months ago, End date is the last day of the previous month
                startDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth() - 6,
                  1,
                  0,
                  0,
                  0,
                  0
                ); // 1st day of the month 6 months ago
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  0,
                  23,
                  59,
                  59,
                  999
                ); // Last day of the previous month
                break;

              case "Custom":
                // Start and End date from user input
                startDate = new Date(this.customFromDate); // Custom From Date
                endDate = new Date(this.customToDate); // Custom To Date

                // Normalize time to the entire day range
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            }

            // Normalize visit date for comparison
            if (visit?.date_time) {
              const visitDate = new Date(visit.date_time);
              visitDate.setHours(0, 0, 0, 0); // Normalize visit date to midnight

              // Check if visitDate falls within the range
              if (!(visitDate >= startDate && visitDate <= endDate)) {
                includeConditions = false;
              }
            } else {
              includeConditions = false; // Exclude if visit date is invalid
            }
          }

          const includesSearch = ["visiting_person_name"].some((property) => {
            const nestedPropertyValue = property
              .split(".")
              .reduce((acc, current) => acc && acc[current], visit);
            return (
              nestedPropertyValue &&
              nestedPropertyValue
                .replace(/[-_ :.]/g, "")
                .toLowerCase()
                .includes(searchValue)
            );
          });

          return this.search !== ""
            ? includeConditions && includesSearch
            : includeConditions;
        }
      );
    } else {
      this.locationTrackingDetails = this.locationTrackingDetailsCpy?.filter(
        (location: LocationTrackerModel) => {
          let includeConditions = true; // Initialize shouldKeep as true

          if (this.isDateFilterSelected) {
            let startDate: Date;
            let endDate: Date;
            const todaysDate = new Date();

            switch (this.selectedDateFilter) {
              case "Today":
                // Start and End date for Today
                startDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  todaysDate.getDate(),
                  0,
                  0,
                  0,
                  0
                ); // Midnight today
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  todaysDate.getDate(),
                  23,
                  59,
                  59,
                  999
                ); // End of today
                break;

              case "1W":
                // Last week: Start date 7 days ago from yesterday, End date yesterday
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  todaysDate.getDate() - 1,
                  23,
                  59,
                  59,
                  999
                ); // Yesterday
                startDate = new Date(
                  endDate.getFullYear(),
                  endDate.getMonth(),
                  endDate.getDate() - 6,
                  0,
                  0,
                  0,
                  0
                ); // 7 days before yesterday
                break;

              case "1M":
                // Last full month: Start date is the 1st day of the previous month, End date is the last day of the previous month
                startDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth() - 1,
                  1,
                  0,
                  0,
                  0,
                  0
                ); // 1st day of the last month
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  0,
                  23,
                  59,
                  59,
                  999
                ); // Last day of the last month
                break;

              case "6M":
                // Last 6 months: Start date is the 1st day of the month 6 months ago, End date is the last day of the previous month
                startDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth() - 6,
                  1,
                  0,
                  0,
                  0,
                  0
                ); // 1st day of the month 6 months ago
                endDate = new Date(
                  todaysDate.getFullYear(),
                  todaysDate.getMonth(),
                  0,
                  23,
                  59,
                  59,
                  999
                ); // Last day of the previous month
                break;

              case "Custom":
                // Start and End date from user input
                startDate = new Date(this.customFromDate); // Custom From Date
                endDate = new Date(this.customToDate); // Custom To Date

                // Normalize time to the entire day range
                startDate.setHours(0, 0, 0, 0);
                endDate.setHours(23, 59, 59, 999);
                break;
            }

            // Normalize visit date for comparison
            if (location?.atFarmOnDateTime) {
              const visitDate = new Date(location.atFarmOnDateTime);
              visitDate.setHours(0, 0, 0, 0); // Normalize visit date to midnight

              // Check if visitDate falls within the range
              if (!(visitDate >= startDate && visitDate <= endDate)) {
                includeConditions = false;
              }
            } else {
              includeConditions = false;
            }
          }

          return includeConditions;
        }
      );
    }

  }

  /**
   * @method onMapReady
   * @description Callback when Google Map is ready. Initializes map bounds and markers.
   * Handles duplicate coordinates by applying a small offset.
   * @param {google.maps.Map} map - The Google Map instance.
   */
  onMapReady(map: google.maps.Map) {
    if (!map) {
      console.error("Google Map reference is undefined!");
      return;
    }

    this.map = map;  // Store reference to Google Map

    const bounds = new google.maps.LatLngBounds();
    const offset = 0.0001;
    const adjustedCoordinates = new Map<string, { lat: number; lng: number }>();

    this.locationTrackingDetails.forEach((location) => {
      let lat = parseFloat(location.atFarmOnlatitude);
      let lng = parseFloat(location.atFarmOnlongitude);

      if (isNaN(lat) || isNaN(lng)) {
        console.error("Invalid lat/lng:", location);
        return;
      }

      let key = `${lat},${lng}`;

      while (adjustedCoordinates.has(key)) {
        lat += offset;
        lng += offset;
        key = `${lat},${lng}`;
      }

      adjustedCoordinates.set(key, { lat, lng });

      location.atFarmOnlatitude = lat;
      location.atFarmOnlongitude = lng;

      bounds.extend(new google.maps.LatLng(lat, lng));
    });

    this.map.googleMap?.fitBounds(bounds);

    // Limit zoom to 20
    const listener = this.map.googleMap?.addListener('idle', () => {
      if (this.map.googleMap?.getZoom()! > 20) {
        this.map.googleMap.setZoom(20);
      }
      google.maps.event.removeListener(listener!);
    });
  }

  /**
   * @method applySearchFilter
   * @description Applies only text search filter to the visits list.
   * @param {string} searchValue - Normalized search string.
   * @side-effect Updates `providerVisitsList`.
   */
  public applySearchFilter(searchValue: string) {
    this.providerVisitsList = this.providerVisitsListCpy.filter(
      (visit: ProviderVisitsModel) =>
        ["visiting_person_name"].some((property) => {
          const nestedPropertyValue = property
            .split(".")
            .reduce((acc, current) => acc && acc[current], visit);
          return (
            nestedPropertyValue &&
            nestedPropertyValue
              .replace(/[-_ :.]/g, "")
              .toLowerCase()
              .includes(searchValue)
          );
        })
    );
  }

  /**
   * @method toggleSortOrder
   * @description Toggles the sorting order (Ascending/Descending) for visits list.
   * @side-effect Updates `isSortedAsc`, calls `sortVisitsList`.
   */
  public toggleSortOrder(): void {
    this.isSortedAsc = !this.isSortedAsc; // Toggle the sort order
    this.sortVisitsList(); // Call the sorting method
  }

  /**
   * @method sortVisitsList
   * @description Sorts the visits list based on `date_time`.
   * @side-effect Sorts `providerVisitsList`.
   */
  public sortVisitsList(): void {
    const sortOrder = this.isSortedAsc ? 1 : -1;
    this.providerVisitsList.sort((a, b) => {
      const property = "date_time"; // Change this to the property you want to sort by
      if (a[property] < b[property]) return -1 * sortOrder;
      if (a[property] > b[property]) return 1 * sortOrder;
      return 0;
    });
  }

  /**
   * @method filterManager
   * @description Filters the provider managers list based on search input.
   * @param {KeyboardEvent} event - Keyboard event.
   */
  public filterManager(event: KeyboardEvent): void {
    const searchValue =
      this.selectedManager &&
      this.selectedManager.replace(/[-_]|\s/g, "").toLowerCase();
    if (searchValue && (event.key != "Backspace" || event.key == "Backspace")) {
      this.$providerManagersList = this.$providerManagersListCpy.filter((obj) =>
        (obj.firstName + " " + obj.lastName)
          .replace(/[-_]|\s/g, "")
          .toLowerCase()
          .includes(searchValue)
      );
    } else if (!searchValue) {
      this.$providerManagersList = this.$providerManagersListCpy;
    }
  }

  /**
   * @method filterTechnician
   * @description Filters the provider technicians list based on search input.
   * @param {KeyboardEvent} event - Keyboard event.
   */
  public filterTechnician(event: KeyboardEvent): void {
    const searchValue =
      this.selectedTechnician &&
      this.selectedTechnician.replace(/[-_]|\s/g, "").toLowerCase();
    if (searchValue && (event.key != "Backspace" || event.key == "Backspace")) {
      this.$providerTechniciansList = this.selectedManagerObj
        ? this.$providerTechniciansListCpy
          .filter(
            (person) => person.reportingTo.id == this.selectedManagerObj.id
          )
          .filter((obj) =>
            (obj.firstName + obj.lastName)
              .replace(/[-_]|\s/g, "")
              .toLowerCase()
              .includes(searchValue)
          )
        : this.$providerTechniciansListCpy.filter((obj) =>
          (obj.firstName + obj.lastName)
            .replace(/[-_]|\s/g, "")
            .toLowerCase()
            .includes(searchValue)
        );
    } else if (!searchValue) {
      this.$providerTechniciansList = this.selectedManagerObj
        ? this.$providerTechniciansListCpy.filter(
          (person) => person.reportingTo.id == this.selectedManagerObj.id
        )
        : this.$providerTechniciansListCpy;
    }
  }

  /**
   * @method closeManagerListDropdown
   * @description Closes the manager dropdown.
   */
  public closeManagerListDropdown() {
    this.managerDropdownOpen = false;
  }

  /**
   * @method closeTechnicianListDropdown
   * @description Closes the technician dropdown.
   */
  public closeTechnicianListDropdown() {
    this.technicianDropdownOpen = false;
  }

  /**
   * @lifecycle ngAfterViewInit
   * @description Lifecycle hook called after view initialization.
   * Sets map height and adds fullscreen listener.
   * @side-effect Sets map height, adds event listener.
   */
  public ngAfterViewInit() {
    // Try setting the height initially with a delay in case of asynchronous rendering
    this.setMapHeightWithRetry();
    // Add listener for full-screen changes
    document.addEventListener(
      "fullscreenchange",
      this.onFullScreenChange.bind(this)
    );
  }

  /**
   * @method setMapHeightWithRetry
   * @private
   * @description Attempts to set the map height with retries if the element is not immediately available.
   * @param {number} retries - Number of retries. Default 10.
   */
  private setMapHeightWithRetry(retries: number = 10, delay: number = 100) {
    const attemptToSetHeight = () => {
      const nativeMapElement = this.getMapNativeElement();

      if (nativeMapElement) {
        this.renderer.setStyle(
          nativeMapElement,
          "height",
          this.isMapViewInFullScreenMode ? "100%" : "360px"
        );
      } else if (retries > 0) {
        setTimeout(() => attemptToSetHeight(), delay);
      } else {
        console.error(
          "Failed to find agm-map element after multiple attempts."
        );
      }
    };

    attemptToSetHeight();
  }

  /**
   * @method getMapNativeElement
   * @private
   * @description Retrieves the native HTML element for the Google Map.
   */
  private getMapNativeElement(): HTMLElement | null {
    if (this.mapElement && this.mapElement["_elementRef"]) {
      // Check if @ViewChild reference is available
      return this.mapElement["_elementRef"].nativeElement;
    } else {
      // Fallback: use querySelector to find agm-map element directly
      return document.querySelector("agm-map") as HTMLElement;
    }
  }

  /**
   * @method openFullScreen
   * @description Toggles fullscreen mode for either the tabular view or map view container.
   */
  public openFullScreen(): void {
    if (this.isTabularView) {
      let element = document.getElementById("visitsList") as HTMLElement;
      if (element.requestFullscreen) {
        element.requestFullscreen();
      }
    } else {
      let element = document.getElementById("mapsContainer") as HTMLElement;
      const nativeMapElement = this.getMapNativeElement();

      if (nativeMapElement) {
        this.renderer.setStyle(nativeMapElement, "height", "100%");
        if (element.requestFullscreen) {
          element.requestFullscreen();
        }
      } else {
        console.error("mapElement is still not available in openFullScreen");
      }
    }
  }

  /**
   * @method onFullScreenChange
   * @private
   * @description Handles fullscreen change events to adjust map height.
   */
  private onFullScreenChange(): void {
    const nativeMapElement = this.getMapNativeElement();

    if (nativeMapElement) {
      // Check if full-screen mode is active
      this.isMapViewInFullScreenMode = !!document.fullscreenElement;

      // Adjust height based on full-screen state
      this.renderer.setStyle(
        nativeMapElement,
        "height",
        this.isMapViewInFullScreenMode ? "100%" : "360px"
      );
    }
  }

  public lastOpenInfoWindow: MapInfoWindow | null = null; // Declare the property

  /**
   * @method onLocationMouseOverClick
   * @description Handles mouse over/click on map markers to show info window.
   * @param {MapMarker} marker - The map marker.
   * @param {any} location - Location data associated with the marker.
   */
  public onLocationMouseOverClick(marker: MapMarker, location: any): void {

    if (!this.infoWindow) {
      console.error("Error: infoWindow is undefined.");
      return;
    }

    // Find the visit details based on location tracker ID
    this.locationTrackingInfoDetails = this.providerVisitsListOriginal.find(
      (visit) => visit.location_tracker_id === location.id
    );

    // Close the previously opened InfoWindow
    if (this.lastOpenInfoWindow) {
      try {
        this.lastOpenInfoWindow.close();
      } catch (error) {
        console.error("Error closing previous InfoWindow:", error);
      }
    }

    // Store the last opened InfoWindow
    this.lastOpenInfoWindow = this.infoWindow;

    // Open the new InfoWindow with the correct anchor (marker)
    setTimeout(() => this.infoWindow.open(marker), 0);
  }

  /**
   * @method resetMapState
   * @description Resets the map state, closing any open info windows.
   */
  public resetMapState(): void {
    if (this.gm?.lastOpen) {
      try {
        this.gm.lastOpen.close();
      } catch (error) {
        console.error("Error closing InfoWindow during reset:", error);
      }
    }

    // Ensure gm is of type GoogleMap before resetting
    if (this.gm instanceof GoogleMap) {
      this.gm = null;
    }
  }

  /**
   * @method showHideStatsContentBlocks
   * @description Toggles visibility of stats content blocks (Products vs Dues).
   * @param {string} by - The block to show ('Products' or 'Dues').
   */
  public showHideStatsContentBlocks(by: string) {
    switch (by) {
      case "Products":
        this.isProductsStatsBlockDisplay = true;
        this.isDueStatsBlockDisplay = false;
        this.isHarvestStatsBlockDisplay = false;
        break;
      case "Dues":
        this.isProductsStatsBlockDisplay = false;
        this.isDueStatsBlockDisplay = true;
        this.isHarvestStatsBlockDisplay = false;
        break;
      default:
        this.isProductsStatsBlockDisplay = false;
        this.isDueStatsBlockDisplay = false;
        this.isHarvestStatsBlockDisplay = true;
        break;
    }
  }

  /**
   * @method showHideVisitDetailsContentBlocks
   * @description Toggles visibility of visit details content blocks (Placements, Movements, etc.).
   * @param {string} by - The block to show.
   */
  public showHideVisitDetailsContentBlocks(by: string) {
    switch (by) {
      case "Placements":
        this.isPlacementsDataDisplay = true;
        this.isMovementsDataDisplay = false;
        this.isReturnsDataDisplay = false;
        this.isPaymentsDataDisplay = false;
        this.isProductsUsageDisplayed = false;
        this.isHarvestSummaryDisplayed = false;
        break;
      case "Movements":
        this.isPlacementsDataDisplay = false;
        this.isMovementsDataDisplay = true;
        this.isReturnsDataDisplay = false;
        this.isPaymentsDataDisplay = false;
        this.isProductsUsageDisplayed = false;
        this.isHarvestSummaryDisplayed = false;
        break;
      case "Returns":
        this.isPlacementsDataDisplay = false;
        this.isMovementsDataDisplay = false;
        this.isReturnsDataDisplay = true;
        this.isPaymentsDataDisplay = false;
        this.isProductsUsageDisplayed = false;
        this.isHarvestSummaryDisplayed = false;
        break;
      case "ListOfProductsUsed":
        this.isPlacementsDataDisplay = false;
        this.isMovementsDataDisplay = false;
        this.isReturnsDataDisplay = false;
        this.isPaymentsDataDisplay = false;
        this.isProductsUsageDisplayed = true;
        this.isHarvestSummaryDisplayed = false;
        break;
      case "harvestSummary":
        this.isPlacementsDataDisplay = false;
        this.isMovementsDataDisplay = false;
        this.isReturnsDataDisplay = false;
        this.isPaymentsDataDisplay = false;
        this.isProductsUsageDisplayed = false;
        this.isHarvestSummaryDisplayed = true;
        break;
      case "Payments":
        this.isPlacementsDataDisplay = false;
        this.isMovementsDataDisplay = false;
        this.isReturnsDataDisplay = false;
        this.isPaymentsDataDisplay = true;
        this.isProductsUsageDisplayed = false;
        this.isHarvestSummaryDisplayed = true;
        break;
      default:
        break;
    }
  }

  /**
   * @method isVisitsInMobileView
   * @description Checks if the user is on a mobile device.
   * @returns {boolean} True if mobile device.
   */
  isVisitsInMobileView(): boolean {
    const isMobile = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent);
    return !isMobile;
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook called when component is destroyed.
   * Cancels ongoing HTTP requests and removes event listeners.
   */
  public ngOnDestroy(): void {
    this.personsService.cancelOnGoingHttpRequests();
    this.providerVisitsService.cancelOnGoingHttpRequests();
    this.providerVisitsStatsService.cancelOnGoingHttpRequests();
    this.locationTrackerService.cancelOnGoingHttpRequests();
    this.productPlacementsService.cancelOnGoingHttpRequests();
    // Clean up event listener when component is destroyed
    document.removeEventListener(
      "fullscreenchange",
      this.onFullScreenChange.bind(this)
    );
  }
}
